<template>
  <v-row no-gutters class="fill-height flex-column pr-5 pb-5">
    <v-col v-if="mapPanelDataFetched">
      <card-title-row class="pl-0 pt-5" translation-path="sidebar.waterHammer.title" :has-buttons="false" />
      <steps-wizard
        ref="steps-wizard"
        :steps="steps"
        :inputs="inputs"
        :params-proxy.sync="params"
        :is-analysis-done-proxy.sync="isAnalysisDone"
        :is-analysis-loading="isAnalysisLoading"
        :wizard-action-handler="turnOnAnalysis"
        :custom-reset-handler="customResetHandler"
      >
        <template #stepPrepend-1>
          <v-row class="mb-2 mt-0 mx-0">
            <v-col class="text-left px-0 pb-0">
              <v-tooltip bottom :disabled="pipesAdded && !isPipeSelected" max-width="300">
                <template #activator="{ on }">
                  <span v-on="on">
                    <data-input
                      simple
                      ref="pipeIdButton"
                      v-bind="featuresInputProps"
                      :featuresSettings="featuresSettings"
                      @pushFeatures="selectPipe"
                    />
                  </span>
                </template>
                {{ buttonTooltip }}
              </v-tooltip>
            </v-col>
          </v-row>
          <template v-if="isPipeSelected">
            <v-row class="mb-4">
              <v-col>
                <v-list class="py-0">
                  <template v-for="feature in selectedPipes || []">
                    <v-list-item :key="feature" class="hover-list-element">
                      <v-list-item-content class="pa-0">
                        <v-list-item-title>
                          <v-subheader class="pl-0 font--text text-body-2" style="height: auto;">
                            ID: {{ feature }}
                          </v-subheader>
                        </v-list-item-title>
                      </v-list-item-content>
                      <v-list-item-action>
                        <v-icon @click="deleteFeature(feature)" color="rgba(0,0,0,.7)" size="19px">
                          mdi-close
                        </v-icon>
                      </v-list-item-action>
                    </v-list-item>
                  </template>
                </v-list>
              </v-col>
            </v-row>
          </template>
        </template>
        <template #analysisDoneCallback>
          <v-row style="align-items: center; gap: 0.5rem 1rem" class="mb-5" no-gutters>
            <v-col cols="auto" class="text-left">
              {{ $i18n.t('sidebar.waterHammer.increasedPressureWaveVelocity') }}:
            </v-col>
            <v-col cols="auto" class="text-left">
              <v-chip label>{{ parseFloat(calcResults.waveVelocity.toFixed(5)) }} [m/s]</v-chip>
            </v-col>
          </v-row>
          <v-row style="align-items: center; gap: 0.5rem 1rem" class="mb-5" no-gutters>
            <v-col cols="auto" class="text-left"> {{ $i18n.t('sidebar.waterHammer.pressureIncrease') }}: </v-col>
            <v-col cols="auto" class="text-left">
              <v-row no-gutters style="align-items: center; gap: 0.5rem">
                <v-col cols="auto">
                  <v-chip label>{{ parseFloat(calcResults.pressure.toFixed(5)) }} [Pa]</v-chip>
                </v-col>
                <v-col cols="auto">=</v-col>
                <v-col cols="auto">
                  <v-chip label>{{ parseFloat((calcResults.pressure / 100000).toFixed(5)) }} [bar]</v-chip>
                </v-col>
                <v-col cols="auto">=</v-col>
                <v-col cols="auto">
                  <v-chip label
                    >{{ parseFloat((calcResults.pressure * 0.00010197442889221).toFixed(5)) }} [m
                    H<sub>2</sub>O]</v-chip
                  >
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </template>
      </steps-wizard>
    </v-col>
    <main-loader v-else />
  </v-row>
</template>

<script>
import CardTitleRow from '@/components/CardTitleRow';
import MainLoader from '@/components/MainLoader';
import { get, call } from 'vuex-pathify';
import StepsWizard from '@/components/StepsWizard';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
export default {
  name: 'WaterHammer',
  mixins: [prepareAttributesFeatureCardMixin],
  components: {
    CardTitleRow,
    StepsWizard,
    MainLoader,
    DataInput: () => import('@/components/DataInput'),
  },
  data: () => ({
    params: {
      water_density: 1000,
      liquid_elasticity: 2000000000,
    },
    currentPipeLayerId: undefined,
    isAnalysisLoading: false,
    isAnalysisDone: false,
    pipeAttributes: {},
    calcResults: {
      waveVelocity: undefined,
      pressure: undefined,
    },
    materialElasticityDict: {
      Polietylen: 1200000000,
      Stal: 200000000000,
      Żeliwo: 200000000000,
      PVC: 3000000000,
    },
  }),
  watch: {
    mapPanelDataFetched(nV) {
      if (!nV) return;
      this.getPipeAttributes();
    },
    currentPipeLayer: {
      handler(nV) {
        if (!nV) return;
        this.getPipeAttributes();
      },
      immediate: true,
    },
    selectedPipe: {
      handler: async function(nV) {
        if (!nV) return;
        await this.$nextTick();
        const selectedPipeProperties = (
          await this.getLayerFeature({
            layer_id: this.currentPipeLayerId,
            feature_id: nV,
            only_attributes: true,
          })
        ).properties;
        if (selectedPipeProperties[this.waterHammerMetadata.material])
          this.$set(this.params, 'pipe_material', selectedPipeProperties[this.waterHammerMetadata.material]);
        if (selectedPipeProperties[this.waterHammerMetadata.model_diameter])
          this.$set(this.params, 'pipe_diameter', selectedPipeProperties[this.waterHammerMetadata.model_diameter]);
        if (selectedPipeProperties[this.waterHammerMetadata.thickness])
          this.$set(this.params, 'pipe_thickness', selectedPipeProperties[this.waterHammerMetadata.thickness]);
      },
    },
    'params.pipe_material': function(nV) {
      if (this.materialElasticityDict[nV]) {
        this.params['pipe_material_elasticity'] = this.materialElasticityDict[nV];
      }
    },
  },
  computed: {
    selectedPipes: get('map/selectedFeatures@:currentPipeLayerId'),
    projectElements: get('layers/project@layers'),
    dataSources: get('layers/metadata'),
    mapPanelDataFetched: get('map/mapPanelDataFetched'),
    activeTool: get('tools/activeTool'),
    layers: get('layers/layers'),
    waterHammerMetadata: get('admin/modulesMapping@water_hammer'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
    computedSelectedPipes() {
      return this.selectedPipes || [];
    },
    selectedPipe() {
      return this.computedSelectedPipes[0] || undefined;
    },
    isPipeSelected() {
      return this.selectedPipe ? true : false;
    },
    currentPipeLayer() {
      return this.pipesLayers.find(layer => layer.id === this.currentPipeLayerId);
    },
    pipesLayers() {
      return this.projectLayers.filter(layer => layer.data_source_name === 'water_pipes');
    },
    pipesAdded() {
      return this.pipesLayers?.length > 0;
    },
    buttonTooltip() {
      return this.pipesAdded
        ? this.$i18n.t('sidebar.waterHammer.onlyOnePipe')
        : this.$i18n.t('sidebar.waterHammer.pipesNotAdded');
    },
    steps() {
      return [
        {
          step: 1,
          translationPath: 'sidebar.waterHammer.pipes',
          nextDisabled: !this.isPipeSelected,
        },
        {
          step: 2,
          translationPath: 'sidebar.waterHammer.parameters',
        },
        {
          step: 3,
          translationPath: 'sidebar.waterHammer.analysis',
        },
      ];
    },
    featuresInputProps() {
      return {
        name: 'pipe_id',
        step: 1,
        translationPath: 'sidebar.waterHammer.selectPipe',
        dataType: { name: 'features' },
        disabled: !this.pipesAdded || this.isPipeSelected,
      };
    },
    featuresSettings() {
      return {
        layers: {
          included: [...this.pipesLayers?.map(layer => layer.id)],
        },
      };
    },
    inputs() {
      return [
        {
          name: 'pipe_material',
          step: 1,
          translationPath: 'sidebar.waterHammer.pipeMaterial',
          ...this.defaultInputProps,
          ...this.pipeAttributes[this.waterHammerMetadata.material],
          rules: [
            ...(this.pipeAttributes[this.waterHammerMetadata.model_diameter]?.rules || []),
            ...this.defaultInputProps.rules,
          ],
          label: null,
          disabled: !this.isPipeSelected,
          tooltip: !this.isPipeSelected ? this.$i18n.t('sidebar.waterHammer.selectOnePipe') : null,
        },
        {
          name: 'pipe_thickness',
          step: 1,
          translationPath: 'sidebar.waterHammer.pipeThickness',
          ...this.defaultInputProps,
          ...this.pipeAttributes[this.waterHammerMetadata.thickness],
          rules: [
            ...(this.pipeAttributes[this.waterHammerMetadata.model_diameter]?.rules || []),
            ...this.defaultInputProps.rules,
          ],
          label: null,
          disabled: !this.isPipeSelected,
          tooltip: !this.isPipeSelected ? this.$i18n.t('sidebar.waterHammer.selectOnePipe') : null,
        },
        {
          name: 'pipe_diameter',
          step: 1,
          translationPath: 'sidebar.waterHammer.pipeDiameter',
          ...this.defaultInputProps,
          ...this.pipeAttributes[this.waterHammerMetadata.model_diameter],
          rules: [
            ...(this.pipeAttributes[this.waterHammerMetadata.model_diameter]?.rules || []),
            ...this.defaultInputProps.rules,
          ],
          label: null,
          disabled: !this.isPipeSelected,
          tooltip: !this.isPipeSelected ? this.$i18n.t('sidebar.waterHammer.selectOnePipe') : null,
        },
        {
          name: 'water_density',
          step: 2,
          translationPath: 'sidebar.waterHammer.waterDensity',
          ...this.defaultInputProps,
        },
        {
          name: 'liquid_elasticity',
          step: 2,
          translationPath: 'sidebar.waterHammer.liquidElasticity',
          ...this.defaultInputProps,
        },
        {
          name: 'pipe_material_elasticity',
          step: 2,
          translationPath: 'sidebar.waterHammer.pipeMaterialElasticity',
          ...this.defaultInputProps,
        },
        {
          name: 'pipe_flow_velocity_delta',
          step: 2,
          translationPath: 'sidebar.waterHammer.pipeFlowVelocityDelta',
          ...this.defaultInputProps,
        },
      ];
    },
    defaultInputProps() {
      return {
        dataType: { name: 'float' },
        rules: [v => !!v || [false, 0].includes(v) || this.$i18n.t('rules.required')],
        cols: '7',
        nameCols: '5',
      };
    },
  },
  methods: {
    getLayerFeature: call('layers/getLayerFeature'),
    selectPipe(features) {
      if (!features || features.length === 0) {
        return;
      }
      const featureId = features[0].id;
      this.currentPipeLayerId = features[0].layerId;
      this.$refs[`pipeIdButton`]?.$refs['data-input']?.toggleOff();
      this.$store.set('map/ADD_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [this.currentPipeLayerId]: [featureId],
        },
      });
      this.$root.$emit('setProjectLayerStyle', this.currentPipeLayerId, {
        selectedColors: { selectedColor: '#F00', haloColorSelected: '#F00' },
      });
    },
    getPipeAttributes() {
      if (!this.mapPanelDataFetched || !this.currentPipeLayer) return;
      const dataSourceAttributes = this.dataSources[this.currentPipeLayer?.data_source_name]?.attributes_schema
        .attributes;
      const schemaForm = this.getForm(this.layers[this.currentPipeLayerId]?.form_schema.elements, dataSourceAttributes);
      this.pipeAttributes = Object.fromEntries(
        this.$_flattenStructure(schemaForm, 'elements')
          .filter(attr =>
            [
              this.waterHammerMetadata.material,
              this.waterHammerMetadata.thickness,
              this.waterHammerMetadata.model_diameter,
            ].includes(attr.attribute)
          )
          .map(foundAttribute => [foundAttribute.attribute, foundAttribute])
      );
    },
    deleteFeature(id, layerId = this.currentPipeLayerId) {
      this.$root.$emit('setProjectLayerStyle', this.currentPipeLayerId);
      this.currentPipeLayerId = undefined;
      const deletedIds = id ? [id] : [];
      this.$store.set('map/DELETE_SELECTED_FEATURES!', {
        type: 'selectedFeatures',
        features: {
          [layerId]: deletedIds,
        },
      });
    },
    customResetHandler() {
      this.deleteFeature(this.selectedPipe);
      this.params = {};
      this.$nextTick(() => {
        this.params['water_density'] = 1000;
        this.params['liquid_elasticity'] = 2000000000;
      });
    },
    turnOnAnalysis() {
      const c =
        1 /
        Math.sqrt(
          this.params.water_density *
            (1 / this.params.liquid_elasticity +
              1 * (this.params.pipe_diameter / (this.params.pipe_material_elasticity * this.params.pipe_thickness)))
        );
      const dp = this.params.water_density * c * this.params.pipe_flow_velocity_delta;
      this.calcResults.waveVelocity = c;
      this.calcResults.pressure = dp;
      this.isAnalysisDone = true;
    },
  },
  beforeDestroy() {
    if (this.selectedPipe) this.deleteFeature(this.selectedPipe);
  },
};
</script>

<style lang="scss" scoped>
.v-list {
  @include border();
  .v-list-item {
    &:not(:last-child) {
      @include border(bottom);
    }
  }
}
::v-deep .v-chip:before {
  background: none !important;
}
</style>
